import { Header } from "@tanstack/react-table";
import {
  TW_DataTableGridHeaderColumnResizer,
  TW_DataTableGridHeaderColumnResizerBar,
  TW_DataTableGridHeaderColumnResizerBarActive,
  TW_DataTableGridHeaderColumnResizerBarCentered,
  TW_DataTableGridHeaderColumnResizerBarLast,
  TW_DataTableGridHeaderColumnResizerCentered,
  TW_DataTableGridHeaderColumnResizerGuide,
  TW_DataTableGridHeaderColumnResizerLast,
} from "@utils/twClasses";
import { CSSProperties } from "react";
import { useModelStore } from "src/store";

const MAIN_BUTTON = 0;

interface ColumnResizerProps<TData, TValue> {
  header: Header<TData, TValue>;
  // The grab zone of the last column stays inside the table: overflowing on the
  // right would widen the scrollable area of the grid.
  isLastColumn: boolean;
  // Height of the guide line drawn through the grid during a resize
  guideHeight: number;
}

// ColumnResizer separates what the user grabs from what the user sees: a wide
// invisible zone centered on the column boundary (it overflows on the next
// header, see headerZIndex), holding a thin bar highlighted without changing the
// zone.
export function ColumnResizer<TData, TValue>({ header, isLastColumn, guideHeight }: ColumnResizerProps<TData, TValue>) {
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const wsIsDarkColor = useModelStore(state => state.wsIsDarkColor);

  if (header.column.getCanResize() === false) return <></>;

  const resize = header.getResizeHandler();
  const isResizing = header.column.getIsResizing();
  // A light workspace color would not stand out from the header background
  const style = { "--resizer-color": wsIsDarkColor ? wsColor : wsDarkColor } as CSSProperties;
  const barClass = isResizing ? TW_DataTableGridHeaderColumnResizerBarActive : TW_DataTableGridHeaderColumnResizerBar;

  return (
    <div
      className={TW_DataTableGridHeaderColumnResizer + " " + (isLastColumn ? TW_DataTableGridHeaderColumnResizerLast : TW_DataTableGridHeaderColumnResizerCentered)}
      style={style}
      // The capture keeps the events, the cursor and the hover on the handle
      // during the whole drag, wherever the pointer goes.
      onPointerDown={(event) => {
        if (event.button === MAIN_BUTTON) {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
      }}
      onMouseDown={(event) => {
        if (event.button === MAIN_BUTTON) {
          resize(event);
        }
      }}
      onTouchStart={resize}
    >
      <div className={barClass + " " + (isLastColumn ? TW_DataTableGridHeaderColumnResizerBarLast : TW_DataTableGridHeaderColumnResizerBarCentered)}>
        {isResizing && <div className={TW_DataTableGridHeaderColumnResizerGuide} style={{ height: guideHeight }} />}
      </div>
    </div>
  );
}
